import type { AgentHarness, HarnessToolDefinition } from "../harness/index.js";
import type { AgentConnection } from "../connection/agent-connection.js";
import type { AgentRunnerConfig } from "../runner-types.js";
import type { AgentMode, TaskContext } from "@project/shared";
import { buildCommonTools, buildForceUpdateTaskStatusTool } from "./common-tools.js";
import { buildPmTools, buildUpdateTaskTool } from "./pm-tools.js";
import { buildDiscoveryTools } from "./discovery-tools.js";
import { buildCodeReviewTools } from "./code-review-tools.js";

// Re-export helpers
export { textResult, imageBlock } from "./helpers.js";

// ── Mode-based tool selection ─────────────────────────────────────────

function getTaskModeTools(agentMode: AgentMode | undefined, connection: AgentConnection) {
  // Building is included so auto tasks (which boot straight into building)
  // can document their plan into the card via update_task_plan as they work.
  if (agentMode === "discovery" || agentMode === "auto" || agentMode === "building") {
    return [buildUpdateTaskTool(connection)];
  }
  return [];
}

function getModeTools(
  agentMode: AgentMode | undefined,
  connection: AgentConnection,
  config: AgentRunnerConfig,
  context?: TaskContext,
) {
  if (config.mode === "pack") {
    // Pack runners orchestrate children regardless of the agentMode axis:
    // subtask CRUD + start_child_cloud_build/stop_child_build/
    // approve_and_merge_pr are their whole job (they never build code).
    return buildPmTools(connection, { includePackTools: true });
  }
  if (config.mode === "task") return getTaskModeTools(agentMode, connection);

  switch (agentMode) {
    case "building":
      return context?.isParentTask ? buildPmTools(connection, { includePackTools: true }) : [];
    case "review":
    case "auto":
    case "discovery":
    case "help":
      return buildPmTools(connection, {
        includePackTools: !!context?.isParentTask,
      });
    default:
      return config.mode === "pm" ? buildPmTools(connection, { includePackTools: false }) : [];
  }
}

// ── Tool assembly ─────────────────────────────────────────────────────

/**
 * Hot protocol tools preloaded into the model's prompt instead of deferred
 * behind ToolSearch. A 24h fleet transcript audit (2026-07-11) found ~350
 * ToolSearch calls/day, nearly all `select:`-loading these same conveyor
 * tools — every session paid 1–8 round trips just to load its own protocol
 * tools (and several wasted an extra call on bare names). Marking them
 * `alwaysLoad` eliminates those round trips on both harnesses.
 *
 * Keep this the intersection of "called by virtually every session in the
 * mode that exposes it" — the long tail (attachments, dependency CRUD,
 * per-test checklist edits) stays deferred so the prompt stays small. Names
 * not exposed in the current mode are simply absent from the built list, so
 * the set intersects with mode gating for free.
 */
export const ALWAYS_LOADED_TOOLS: ReadonlySet<string> = new Set([
  // Every mode/session
  "post_to_chat",
  "read_task_chat",
  "get_task",
  "get_current_plan",
  "set_manual_tests",
  "create_pull_request",
  // Planning/auto/building
  "update_task_plan",
  "update_task_properties",
  // Review mode
  "approve_code_review",
  "request_code_changes",
  // Pack/parent orchestration — a 12h fleet audit (2026-07-14) found a pack
  // runner re-ToolSearching this exact set on EVERY wake (~28 redundant round
  // trips in one session). The whole orchestration loop (list → promote/assign
  // → fire → merge/stop) is called by virtually every pack session.
  "list_subtasks",
  "update_subtask",
  "start_child_cloud_build",
  "stop_child_build",
  "approve_and_merge_pr",
]);

function withAlwaysLoad(tools: HarnessToolDefinition[]): HarnessToolDefinition[] {
  return tools.map((tool) =>
    ALWAYS_LOADED_TOOLS.has(tool.name) ? { ...tool, alwaysLoad: true } : tool,
  );
}

/** Collect all tools for the current mode as harness-neutral definitions. */
export function buildConveyorTools(
  connection: AgentConnection,
  config: AgentRunnerConfig,
  context?: TaskContext,
  agentMode?: AgentMode,
): HarnessToolDefinition[] {
  const effectiveMode = agentMode ?? context?.agentMode ?? undefined;

  const commonTools = buildCommonTools(connection, config);
  const modeTools = getModeTools(effectiveMode, connection, config, context);

  // Building included so auto tasks (booted straight into building) can
  // refine title/story points via update_task_properties while they work.
  // Chat included so a chat card can refine its own title.
  const discoveryTools =
    effectiveMode === "discovery" ||
    effectiveMode === "auto" ||
    effectiveMode === "building" ||
    effectiveMode === "chat"
      ? buildDiscoveryTools(connection)
      : [];

  // Code review tools available in review mode
  const codeReviewTools = effectiveMode === "review" ? buildCodeReviewTools(connection) : [];

  const emergencyTools = [buildForceUpdateTaskStatusTool(connection)];

  const tools = withAlwaysLoad([
    ...commonTools,
    ...modeTools,
    ...discoveryTools,
    ...codeReviewTools,
    ...emergencyTools,
  ]);

  // Chat cards have no PR workflow — the create_pull_request tool must not be
  // exposed. Completion happens via force_update_task_status("Complete").
  if (effectiveMode === "chat") {
    return tools.filter((tool) => tool.name !== "create_pull_request");
  }

  return tools;
}

// ── MCP server factory ────────────────────────────────────────────────

// oxlint-disable-next-line typescript/explicit-function-return-type
export function createConveyorMcpServer(
  harness: AgentHarness,
  connection: AgentConnection,
  config: AgentRunnerConfig,
  context?: TaskContext,
  agentMode?: AgentMode,
) {
  return harness.createMcpServer({
    name: "conveyor",
    tools: buildConveyorTools(connection, config, context, agentMode),
  });
}
